import { type OperationId, operationIds } from "@gen/app-access-config";
import {
  CoreQuery,
  type ITracking,
  Track_transaction_body,
} from "@gen/app-query";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";

import { ClearableSelectWithPlaceholder } from "~/components/select_with_placeholder";
import { TextInput } from "~/components/text_input";
import { useAuthOperations } from "~/contexts/auth_query";

interface SetTrackingDetailsActionProps {
  canExecuteForStatus: (operationId: OperationId) => boolean;
  transactionId: number;
  tracking?: ITracking;
}

interface FormValues {
  carrier: string;
  tracking_code: string;
}

export function SetTrackingDetailsAction({
  canExecuteForStatus,
  transactionId,
  tracking,
}: SetTrackingDetailsActionProps) {
  const { canExecuteAdminOperation } = useAuthOperations();

  const queryClient = useQueryClient();

  const { data: carriersData } = CoreQuery.useAdmin_getSupportedCarriersQuery();
  let carrier = "";
  if (tracking?.carrier) {
    carrier =
      carriersData?.carriers.find(({ code }) => tracking.carrier === code)
        ?.name ?? tracking.carrier;
  }

  const { control, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: {
      carrier: carrier,
      tracking_code: tracking?.tracking_code,
    },
  });

  const { mutate: trackOnline, isPending } =
    CoreQuery.useAdmin_trackOnlineTransactionMutation(transactionId, {
      meta: { success: "Tracking details saved" },
      onSuccess: () => {
        if (transactionId) {
          queryClient.invalidateQueries({
            queryKey:
              CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
          });
        }
      },
    });

  const {
    mutate: extendTrackingDeadline,
    isPending: isTrackingDeadlineExtending,
  } = CoreQuery.useAdmin_extendOnlineTrackingDeadlineMutationWithParameters({
    meta: {
      success: "Extended tracking deadline",
    },
  });

  const canSubmit =
    formState.isDirty &&
    canExecuteForStatus(operationIds["core_admin.trackOnlineTransaction"]);

  const onSubmit = (values: FormValues) => {
    trackOnline(
      Track_transaction_body.fromJS({
        carrier: values.carrier,
        tracking_code: values.tracking_code,
      }),
    );
  };

  const isReadOnly =
    canExecuteAdminOperation(operationIds["core_admin.getOnlineTransaction"]) &&
    tracking !== undefined;

  if (
    !isReadOnly &&
    !canExecuteForStatus(operationIds["core_admin.trackOnlineTransaction"])
  ) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={5}>
        <Typography variant="body1DashedDivider">Tracking</Typography>
        <Controller
          name="tracking_code"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              disabled={isReadOnly}
              label="Tracking code"
              data-testid="tracking-code-input"
              fullWidth
              clearable={!isReadOnly}
              onClear={() => field.onChange("")}
            />
          )}
        />

        <Controller
          name="carrier"
          control={control}
          render={({ field }) => (
            <FormControl size="small">
              <InputLabel>Carrier</InputLabel>
              <ClearableSelectWithPlaceholder
                disabled={isReadOnly}
                {...field}
                label="carrier"
                placeholder="Choose carrier"
                data-testid="tracking-carrier-select"
                isClearable={!isReadOnly}
                onClear={() => field.onChange("")}
              >
                {carriersData?.carriers.map(({ code, name }) => (
                  <MenuItem
                    key={code}
                    value={code}
                    data-testid={`carrier-${code}-option`}
                  >
                    {name}
                  </MenuItem>
                ))}
              </ClearableSelectWithPlaceholder>
            </FormControl>
          )}
        />

        {!isReadOnly && (
          <>
            <Button
              variant="contained"
              size="large"
              type="submit"
              fullWidth
              disabled={!canSubmit}
              loading={isPending}
              data-testid="save-tracking-button"
            >
              Save tracking details
            </Button>
            <Button
              variant="text"
              size="large"
              fullWidth
              type="button"
              onClick={() =>
                extendTrackingDeadline({
                  transaction_id: transactionId,
                  extendTrackingDeadlineBody: {},
                })
              }
              loading={isTrackingDeadlineExtending}
              data-testid="extend-tracking-deadline-button"
            >
              Extend tracking submission deadline
            </Button>
          </>
        )}
      </Stack>
    </form>
  );
}
